/**
 * Awareness (BUILDLIST Phase 58, step 1): what a team knows, as one model.
 *
 * Two primitives, for bots and the sector — never a display for the human:
 *   - a TRACK is a contact: where something was, when, how the team learned it,
 *     and how sure it is. Tracks are a set, not a map from enemy id — the number of
 *     enemies may be unknown; a track names a unit only when the observation did.
 *   - COVERAGE is what surface the team has scanned, when, by what — decaying by
 *     age, so a planet seen ten turns ago is not "known".
 *
 * `observe` folds events into a team's awareness. Events are the only way knowledge
 * enters (the fairness rule: a side knows only what it saw, heard or was told), so
 * the invariant "no track without an event" is a property of this module, not a
 * sweep of five. Everything is pure and deterministic; nothing here reads the
 * true game state.
 *
 * Its consumers: the bot fog context (botFogContext.ts) and probe scouting (bot/scouting.ts). The queries at the end
 * are the ones the owner asked the model to answer first: how fresh is what I
 * know, where could it have gone since, and how much of what could be known is
 * fresh — the scanned-area-versus-fresh-contacts balance that drives probe-or-shoot.
 */
import type { Vec3, Sighting } from '../types';
import { vecLength, vecSub, vecNormalize } from '../math';
import { BASE_MOVEMENT_RANGE } from '../constants';
import { dacos, dpow } from './dmath';

export type TrackSource = 'seen' | 'probe' | 'incomingFire' | 'heard' | 'evidence' | 'told' | 'sensor' | 'mark';
export type ScanSource = 'los' | 'probe' | 'sensor';

export interface Track {
  id: number;
  /** The unit this contact is believed to be — only when the observation identified one (line of sight does; a shot from nowhere does not). */
  unitId?: number;
  /** The contact's side, when the observation told it (LOS does; a sound does not). */
  teamIndex?: number;
  planetIndex: number;
  /** Where on the planet, when known; null for a contact in space or an unresolved bearing. */
  surfaceDir: Vec3 | null;
  pos: Vec3;
  /** Turn of the observation. */
  turn: number;
  source: TrackSource;
  /** Confidence at the observation (1 = seen now); decays with age — see `trackConfidence`. */
  baseConfidence: number;
}

export interface PlanetCoverage {
  planetIndex: number;
  /** Fraction of the surface ever revealed (today's fog arrays). */
  revealedFraction: number;
  /** Last turn any scan touched this planet, or null. */
  lastScanTurn: number | null;
  lastScanBy: ScanSource | null;
  scans: number;
}

export interface TeamAwareness {
  teamIndex: number;
  /** Turn of the last observation folded in. */
  turn: number;
  tracks: Track[];
  coverage: Record<number, PlanetCoverage>;
  /** How many enemies exist, when the match tells (today's team sizes); null when unknown — never assumed. */
  knownEnemyCount: number | null;
  nextTrackId: number;
}

export type AwarenessEvent =
  | { kind: 'seen'; unitId: number; teamIndex: number; planetIndex: number; surfaceDir: Vec3 | null; pos: Vec3; turn: number; by: 'los' | 'probe' | 'drone' }
  /** A radar dot (ruling 26): something hostile is here — no identity. A later identified sighting at the same place absorbs it. */
  | { kind: 'contact'; planetIndex: number; surfaceDir: Vec3 | null; pos: Vec3; turn: number; by: 'sensor' }
  | { kind: 'incomingFire'; sourcePos: Vec3; planetIndex: number; surfaceDir: Vec3 | null; turn: number }
  | { kind: 'heard'; pos: Vec3; planetIndex: number; turn: number }
  | { kind: 'evidence'; planetIndex: number; pos: Vec3; turn: number }
  | { kind: 'scan'; planetIndex: number; turn: number; by: ScanSource; revealedFraction?: number }
  | { kind: 'told'; track: Omit<Track, 'id'> }
  | { kind: 'enemyCount'; count: number | null };

/** Confidence at observation by source: what the team saw itself is certain; a bearing or a sound is not. */
export const SOURCE_CONFIDENCE: Record<TrackSource, number> = { seen: 1, probe: 1, incomingFire: 0.5, heard: 0.3, evidence: 0.4, told: 0.8, sensor: 0.9, mark: 0.95 };   // X2: a mark is a commander's deliberate fix
/** Per-turn decay of a track's confidence by source: a sighting ages slower than a guess. */
export const SOURCE_DECAY: Record<TrackSource, number> = { seen: 0.8, probe: 0.8, incomingFire: 0.7, heard: 0.6, evidence: 0.7, told: 0.8, sensor: 0.75, mark: 0.85 };

/**
 * The action clock's decay (WORKBOARD §13, the board's "decay is per turn today"): a half-life in seconds by
 * source. People move a person-length a second, so what you saw yourself is worth walking to for a few seconds,
 * a radar dot (position only, re-swept every look) fades faster, a sound fastest; evidence lasts.
 */
export const SOURCE_HALF_LIFE_S: Record<TrackSource, number> = { seen: 4, probe: 5, incomingFire: 3, heard: 2, evidence: 30, told: 4.8, sensor: 3, mark: 20 };

/** Confidence of a track of this source, `seconds` after it was observed (action clock). */
export function confidenceAfterSeconds(source: TrackSource, seconds: number, base: number = SOURCE_CONFIDENCE[source]): number {
  return base * dpow(0.5, Math.max(0, seconds) / SOURCE_HALF_LIFE_S[source]);
}
/** Two anonymous contacts this close (world units) in the same turn are one contact. */
export const MERGE_RADIUS = 3;
/** A track older than this many turns is not "fresh". */
export const FRESH_TURNS = 1;

export function createAwareness(teamIndex: number, turn: number, knownEnemyCount: number | null = null): TeamAwareness {
  return { teamIndex, turn, tracks: [], coverage: {}, knownEnemyCount, nextTrackId: 1 };
}

function coverageFor(aw: TeamAwareness, planetIndex: number): PlanetCoverage {
  return aw.coverage[planetIndex] ?? { planetIndex, revealedFraction: 0, lastScanTurn: null, lastScanBy: null, scans: 0 };
}

/** Fold events into a team's awareness. Pure: returns a new awareness; the order of events is the order of knowledge. */
export function observe(aw: TeamAwareness, events: readonly AwarenessEvent[]): TeamAwareness {
  let tracks = aw.tracks.slice();
  const coverage: Record<number, PlanetCoverage> = { ...aw.coverage };
  let nextId = aw.nextTrackId;
  let turn = aw.turn;
  let knownEnemyCount = aw.knownEnemyCount;

  const upsert = (t: Omit<Track, 'id'>) => {
    // A newer (or equally new, better-sourced) observation of the same unit replaces the old track
    if (t.unitId !== undefined) {
      const i = tracks.findIndex(x => x.unitId === t.unitId);
      if (i >= 0) {
        const old = tracks[i];
        if (t.turn > old.turn || (t.turn === old.turn && t.baseConfidence >= old.baseConfidence)) tracks[i] = { ...t, id: old.id };
        return;
      }
      // An anonymous contact at the same place this turn was this unit
      const j = tracks.findIndex(x => x.unitId === undefined && x.turn === t.turn && vecLength(vecSub(x.pos, t.pos)) <= MERGE_RADIUS);
      if (j >= 0) { tracks[j] = { ...t, id: tracks[j].id }; return; }
      tracks.push({ ...t, id: nextId++ });
      return;
    }
    // Anonymous: merge into any contact at the same place this turn, else a new contact
    const k = tracks.findIndex(x => x.turn === t.turn && vecLength(vecSub(x.pos, t.pos)) <= MERGE_RADIUS);
    if (k >= 0) {
      const old = tracks[k];
      if (t.baseConfidence > old.baseConfidence && old.unitId === undefined) tracks[k] = { ...t, id: old.id };
      return;
    }
    tracks.push({ ...t, id: nextId++ });
  };

  for (const e of events) {
    switch (e.kind) {
      case 'seen':
        turn = Math.max(turn, e.turn);
        upsert({ unitId: e.unitId, teamIndex: e.teamIndex, planetIndex: e.planetIndex, surfaceDir: e.surfaceDir, pos: e.pos, turn: e.turn, source: e.by === 'probe' ? 'probe' : 'seen', baseConfidence: SOURCE_CONFIDENCE[e.by === 'probe' ? 'probe' : 'seen'] });
        break;
      case 'contact':
        turn = Math.max(turn, e.turn);
        // A radar dot: something hostile is there; who, and which side, it does not say
        upsert({ planetIndex: e.planetIndex, surfaceDir: e.surfaceDir, pos: e.pos, turn: e.turn, source: 'sensor', baseConfidence: SOURCE_CONFIDENCE.sensor });
        break;
      case 'incomingFire':
        turn = Math.max(turn, e.turn);
        // A bearing says where the shot came from, not who fired it
        upsert({ planetIndex: e.planetIndex, surfaceDir: e.surfaceDir, pos: e.sourcePos, turn: e.turn, source: 'incomingFire', baseConfidence: SOURCE_CONFIDENCE.incomingFire });
        break;
      case 'heard':
        turn = Math.max(turn, e.turn);
        upsert({ planetIndex: e.planetIndex, surfaceDir: null, pos: e.pos, turn: e.turn, source: 'heard', baseConfidence: SOURCE_CONFIDENCE.heard });
        break;
      case 'evidence':
        turn = Math.max(turn, e.turn);
        upsert({ planetIndex: e.planetIndex, surfaceDir: null, pos: e.pos, turn: e.turn, source: 'evidence', baseConfidence: SOURCE_CONFIDENCE.evidence });
        break;
      case 'scan': {
        turn = Math.max(turn, e.turn);
        const c = coverageFor({ ...aw, coverage }, e.planetIndex);
        coverage[e.planetIndex] = {
          planetIndex: e.planetIndex,
          revealedFraction: Math.max(c.revealedFraction, Math.min(1, e.revealedFraction ?? c.revealedFraction)),
          lastScanTurn: c.lastScanTurn === null ? e.turn : Math.max(c.lastScanTurn, e.turn),
          lastScanBy: c.lastScanTurn === null || e.turn >= c.lastScanTurn ? e.by : c.lastScanBy,
          scans: c.scans + 1,
        };
        break;
      }
      case 'told':
        turn = Math.max(turn, e.track.turn);
        upsert({ ...e.track, source: 'told', baseConfidence: Math.min(e.track.baseConfidence, SOURCE_CONFIDENCE.told) });
        break;
      case 'enemyCount':
        knownEnemyCount = e.count;
        break;
    }
  }
  return { ...aw, turn, tracks, coverage, knownEnemyCount, nextTrackId: nextId };
}

// -----------------------------------------------------------------------------
// Queries — the questions a commander asks
// -----------------------------------------------------------------------------

/** "Is that still true?" — confidence now, decayed by age and source. */
export function trackConfidence(track: Track, turn: number): number {
  const age = Math.max(0, turn - track.turn);
  return track.baseConfidence * dpow(SOURCE_DECAY[track.source], age);
}

export interface MovementEnvelope {
  /** How far along the surface the contact could have walked since it was seen. */
  surfaceRadius: number;
  /** Whether it could have left the planet (jumped) in the time since. */
  couldHaveLeftPlanet: boolean;
  /** Turns since the observation. */
  age: number;
}

/**
 * "Where could it be now?" — from PUBLIC numbers only: the base movement range and whether units can
 * jump at all. Never the unit's remaining movement points or fuel (the team cannot see those).
 */
export function movementEnvelope(track: Track, turn: number, opts: { walkPerTurn?: number; canJump?: boolean } = {}): MovementEnvelope {
  const age = Math.max(0, turn - track.turn);
  const walk = opts.walkPerTurn ?? BASE_MOVEMENT_RANGE;
  return { surfaceRadius: age * walk, couldHaveLeftPlanet: (opts.canJump ?? true) && age >= 1, age };
}

/** Whether a surface point (as a direction from the planet's centre) is inside a track's envelope now: same planet within the walk radius, or the contact could have jumped. */
export function withinEnvelope(track: Track, turn: number, surfaceDir: Vec3, planetIndex: number, planetRadius: number, opts?: { walkPerTurn?: number; canJump?: boolean }): boolean {
  const env = movementEnvelope(track, turn, opts);
  if (planetIndex !== track.planetIndex) return env.couldHaveLeftPlanet;
  if (!track.surfaceDir) return true;
  const a = vecNormalize(track.surfaceDir), b = vecNormalize(surfaceDir);
  const arc = dacos(Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z))) * planetRadius;
  return arc <= env.surfaceRadius + 1e-9;
}

/** Tracks observed within the last `FRESH_TURNS` turns. */
export function freshTracks(aw: TeamAwareness, turn: number, maxAge = FRESH_TURNS): Track[] {
  return aw.tracks.filter(t => turn - t.turn <= maxAge);
}

/** "Do I know where they are?" — enemies the match says exist minus the distinct units with a fresh identified track; null when the count is unknown. */
export function unaccountedEnemies(aw: TeamAwareness, turn: number, maxAge = FRESH_TURNS): number | null {
  if (aw.knownEnemyCount === null) return null;
  const ids = new Set(freshTracks(aw, turn, maxAge).filter(t => t.unitId !== undefined).map(t => t.unitId));
  return Math.max(0, aw.knownEnemyCount - ids.size);
}

/** Turns since a planet was last scanned; Infinity when never. */
export function coverageAge(aw: TeamAwareness, planetIndex: number, turn: number): number {
  const c = aw.coverage[planetIndex];
  return c && c.lastScanTurn !== null ? Math.max(0, turn - c.lastScanTurn) : Infinity;
}

/** How much of what could be known is fresh: the mean over planets of revealed fraction decayed by scan age (unseen planets count as 0). */
export function freshCoverage(aw: TeamAwareness, planetIndices: readonly number[], turn: number, decayPerTurn = 0.8): number {
  if (planetIndices.length === 0) return 0;
  let sum = 0;
  for (const i of planetIndices) {
    const c = aw.coverage[i];
    if (!c || c.lastScanTurn === null) continue;
    sum += c.revealedFraction * dpow(decayPerTurn, Math.max(0, turn - c.lastScanTurn));
  }
  return sum / planetIndices.length;
}

// -----------------------------------------------------------------------------
// Today's events — the bridge from what the game already records (step 1: no consumers)
// -----------------------------------------------------------------------------

/** The planet a world position belongs to (nearest centre, within twice its radius), or -1. */
function planetAt(pos: Vec3, planets: readonly { position: Vec3; radius: number; alive: boolean }[]): number {
  let best = -1, bestD = Infinity;
  for (let i = 0; i < planets.length; i++) {
    const p = planets[i];
    if (!p.alive) continue;
    const d = vecLength(vecSub(pos, p.position));
    if (d <= p.radius * 2 && d < bestD) { bestD = d; best = i; }
  }
  return best;
}

/**
 * What a team knows today, as events: its members' fog ghosts (last-seen enemy positions), the
 * enemies currently in sight (ids the host computed by line of sight — true positions are legitimately
 * known for those), each planet's revealed fraction and last reveal turn (fog arrays), and backtracked
 * incoming fire as anonymous contacts (the position only — the shooter's id in the intel is not
 * something the team could know). Pure; the caller supplies the sightings and backtracks.
 */
export function eventsFromGameState(
  state: { players: readonly { id: number; teamIndex: number; alive: boolean; planetIndex: number; surfaceDir: Vec3 }[]; planets: readonly { position: Vec3; radius: number; alive: boolean }[]; fog: readonly ({ planets: readonly { revealedVertices: readonly boolean[]; revealTurn: Uint16Array }[]; ghosts: readonly { playerId: number; position: Vec3; turnSeen: number }[] } | undefined)[]; turnNumber: number; sightings?: readonly Sighting[] },
  teamIndex: number,
  visibleEnemyIds: readonly number[],
  enemyWorldPos: (unitId: number) => Vec3,
  backtrackedShots: readonly { sourcePos: Vec3; turn: number }[] = [],
): AwarenessEvent[] {
  const events: AwarenessEvent[] = [];
  const byId = new Map(state.players.map(p => [p.id, p]));
  // Ghosts: every living teammate's last sightings
  for (let i = 0; i < state.players.length; i++) {
    const mate = state.players[i];
    if (!mate.alive || mate.teamIndex !== teamIndex) continue;
    const fog = state.fog[i];
    if (!fog) continue;
    for (const g of fog.ghosts) {
      const enemy = byId.get(g.playerId);
      const planetIndex = planetAt(g.position, state.planets);
      const surfaceDir = planetIndex >= 0 ? vecNormalize(vecSub(g.position, state.planets[planetIndex].position)) : null;
      events.push({ kind: 'seen', unitId: g.playerId, teamIndex: enemy?.teamIndex ?? -1, planetIndex, surfaceDir, pos: g.position, turn: g.turnSeen, by: 'los' });
    }
    // Coverage: the fraction revealed and the last turn anything was revealed, per planet
    for (let pi = 0; pi < fog.planets.length; pi++) {
      const fp = fog.planets[pi];
      let revealed = 0, lastTurn = 0;
      for (let v = 0; v < fp.revealedVertices.length; v++) {
        if (!fp.revealedVertices[v]) continue;
        revealed++;
        if (fp.revealTurn[v] > lastTurn) lastTurn = fp.revealTurn[v];
      }
      if (revealed > 0) events.push({ kind: 'scan', planetIndex: pi, turn: lastTurn, by: 'los', revealedFraction: revealed / fp.revealedVertices.length });
    }
  }
  // In sight now
  for (const id of visibleEnemyIds) {
    const e = byId.get(id);
    if (!e || !e.alive) continue;
    events.push({ kind: 'seen', unitId: id, teamIndex: e.teamIndex, planetIndex: e.planetIndex, surfaceDir: e.surfaceDir, pos: enemyWorldPos(id), turn: state.turnNumber, by: 'los' });
  }
  // I3: the team's instruments (a probe's flight, drones, towers) through one path, the sightings list: an identified
  // sighting is `seen` by its instrument, a radar dot an anonymous contact
  for (const s of state.sightings ?? []) {
    if (s.teamIndex !== teamIndex) continue;
    if (s.unitId !== undefined && s.unitTeam !== undefined) events.push({ kind: 'seen', unitId: s.unitId, teamIndex: s.unitTeam, planetIndex: s.planetIndex, surfaceDir: s.surfaceDir, pos: s.pos, turn: s.turn ?? state.turnNumber, by: s.by === 'probe' ? 'probe' : 'drone' });
    else events.push({ kind: 'contact', planetIndex: s.planetIndex, surfaceDir: s.surfaceDir, pos: s.pos, turn: s.turn ?? state.turnNumber, by: 'sensor' });
  }
  // Shots backtracked to their source: where, never who
  for (const s of backtrackedShots) {
    const planetIndex = planetAt(s.sourcePos, state.planets);
    const surfaceDir = planetIndex >= 0 ? vecNormalize(vecSub(s.sourcePos, state.planets[planetIndex].position)) : null;
    events.push({ kind: 'incomingFire', sourcePos: s.sourcePos, planetIndex, surfaceDir, turn: s.turn });
  }
  return events;
}

// -----------------------------------------------------------------------------
// Information value — "is it worth a turn to find out?"
// -----------------------------------------------------------------------------

export interface ProbeValueOptions {
  /** Radius a shot damages (the weapon's blast radius × the damage factor); a track that may have moved further than this is not a shot. */
  effectiveRadius: number;
  /** Public per-turn walk range for the movement envelope. */
  walkPerTurn?: number;
  /** Coverage decay per turn (see freshCoverage). */
  decayPerTurn?: number;
  /** Whether enemies can leave their planet (jump). False: a tracked enemy is only "findable" on its own planet. */
  canJump?: boolean;
}

/**
 * What a probe over `planetIndex` would tell the team, 0..∞ (≈ 0 nothing, ≈ 1 a shot's worth):
 *   verify — for each track on the planet, how much the contact may have moved beyond a blast's reach since it
 *            was seen, weighted by how much the team still believes it: a stale track is worth refreshing before
 *            a shot is spent on it, a fresh one is a shot;
 *   find   — the share of enemies the team cannot place, times how unlooked-at the planet is (never revealed,
 *            or revealed long ago).
 */
export function probeValue(aw: TeamAwareness, planetIndex: number, turn: number, planetRadius: number, opts: ProbeValueOptions): number {
  let verify = 0;
  for (const t of aw.tracks) {
    if (t.planetIndex !== planetIndex) continue;
    const env = movementEnvelope(t, turn, { walkPerTurn: opts.walkPerTurn });
    const reach = Math.min(env.surfaceRadius, Math.PI * planetRadius);
    const hitChance = reach <= opts.effectiveRadius ? 1 : opts.effectiveRadius / reach;
    verify += trackConfidence(t, turn) * (1 - hitChance);
  }
  const c = aw.coverage[planetIndex];
  const freshness = c && c.lastScanTurn !== null ? c.revealedFraction * dpow(opts.decayPerTurn ?? 0.8, Math.max(0, turn - c.lastScanTurn)) : 0;
  // Enemies that could be on this planet unseen: those with no identified track at all, and those whose track is
  // stale enough that they could have left the planet it was on (never a unit tracked where it stands now)
  let placed = 0;
  const seenIds = new Set<number>();
  for (const t of aw.tracks) {
    if (t.unitId === undefined || seenIds.has(t.unitId)) continue;
    seenIds.add(t.unitId);
    const env = movementEnvelope(t, turn, { walkPerTurn: opts.walkPerTurn, canJump: opts.canJump });
    if (t.planetIndex === planetIndex || !env.couldHaveLeftPlanet) placed++;
  }
  const unaccountedShare = aw.knownEnemyCount === null ? 1 : aw.knownEnemyCount > 0 ? Math.max(0, aw.knownEnemyCount - placed) / aw.knownEnemyCount : 0;
  const find = unaccountedShare * (1 - freshness);
  return verify + find;
}
